/* ============================================================================
   ModalAtividades / index.jsx
   Modal de ATIVIDADES E NOTAS do aluno — portal do responsável.
   Onde os pais veem cada atividade lançada e a nota que o aluno tirou nela.

   Uso no dashboard:
     import ActivitiesModal from '../../components/ModalAtividades'
     const [actOpen, setActOpen] = useState(false)
     <ActivitiesModal
       open={actOpen}
       onClose={() => setActOpen(false)}
       studentId={studentId}
       studentName={student?.name}
       schoolName={schoolName(student)}
       idSchool={student?.id_school?._id}   // opcional
     />

   Fonte de dados: getNumericalGradesByStudentAndYear(studentId, year)
     → { data: [ NumericalGrade ], message }
   Cada NumericalGrade traz:
     - studentGrade  : a nota do aluno na atividade
     - bimonthly     : "1º BIMESTRE" ...
     - id_matter     : { name }            (populado)
     - idActivity    : { descricao, tipo, valor, bimonthly }  (populado)

   Estrutura: um QUADRO por bimestre → dentro, um bloco por matéria →
   dentro, a lista de atividades com a nota do aluno.
   ========================================================================== */

   import React, { useState, useEffect, useRef } from 'react'
   import { IoClose } from 'react-icons/io5'
   import { getNumericalGradesByStudentAndYear, fetchLogo, getSchoolYear } from '../../Api'
   import { useRegimeDoAno } from '../../utils/regimeDoAno'
   import {
       Overlay, ModalCard, TopBar, CloseButton, //PrintButton,
       Body, Loading, Empty, ContLogo, /*Preview,*/ YearBar, InfoBox,
       BimBlock, BimHeader, BimTotals,
       MatterBlock, MatterTitle, MatterSum,
       ActTable, ActHead, ActCell, ActName, ActType, ActGrade, ActValue, NoActivities,
   } from './styles'
   
   /* ---------- helpers --------------------------------------------------------- */
   
   const norm = (s = '') => String(s).trim().toUpperCase()
   
   const bimIndex = (b) => {
       const m = String(b || '').match(/\d+/)
       return m ? parseInt(m[0], 10) - 1 : 99
   }
   
   const toNumber = (v) => {
       if (v == null || v === '') return NaN
       if (typeof v === 'number') return v
       return parseFloat(String(v).replace(',', '.'))
   }
   
   const fmt = (v) => {
       const n = toNumber(v)
       if (isNaN(n)) return '—'
       return n.toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
   }
   
   // agrupa as notas em: bimestre → matéria → atividades
   const groupGrades = (grades, regimeAno = '') => {
       const list = Array.isArray(grades) ? grades : []
   
       // regime — MESMA lógica do boletim: lê o assessmentRegime do quarter
       // populado do 1º PERÍODO (o card de menor bimonthly). A nota pode estar
       // ligada a qualquer um dos quarters, então procura em todos.
       // Sem essa informação, assume BIMESTRAL (padrão).
       let firstGrade = null
       let firstIdx = Infinity
       for (const g of list) {
           const idx = bimIndex(g?.bimonthly)
           if (idx >= 0 && idx < firstIdx) { firstIdx = idx; firstGrade = g }
       }
       const regimeOf = (g) => {
           const q = g?.id_iStQuarter || g?.id_iiNdQuarter || g?.id_iiiRdQuarter || g?.id_ivThQuarter
           return (q && typeof q === 'object' ? (q.assessmentRegime || '') : '')
       }
       let regime = String(regimeOf(firstGrade) || '').trim().toUpperCase()
       // se o 1º período não trouxer o regime, procura em qualquer outra nota
       if (!regime) {
           for (const g of list) {
               const r = regimeOf(g)
               if (r) { regime = String(r).trim().toUpperCase(); break }
           }
       }
       // regime do ANO (vem da escola); sem ele, usa o que vier nos dados
       if (regimeAno) regime = String(regimeAno).trim().toUpperCase()
       const isTrimestral = regime === 'TRIMESTRAL'
       const periodLabel = isTrimestral ? 'Trimestre' : 'Bimestre'
   
       // bimIdx → { idx, matters: Map(matterName → { matter, activities: [] }) }
       const bimMap = new Map()
   
       for (const g of list) {
           const idx = bimIndex(g?.bimonthly)
           if (idx < 0 || idx > 5) continue
   
           if (!bimMap.has(idx)) bimMap.set(idx, { idx, matters: new Map() })
           const bim = bimMap.get(idx)
   
           const matterName = (g?.id_matter?.name || 'Sem matéria').trim()
           const key = norm(matterName)
           if (!bim.matters.has(key)) bim.matters.set(key, { matter: matterName, activities: [] })
   
           const act = g?.idActivity || {}
           bim.matters.get(key).activities.push({
               id: g?._id,
               descricao: (act.descricao || '').trim() || 'Atividade sem descrição',
               tipo: (act.tipo || '').trim(),
               valor: act.valor,
               nota: g?.studentGrade,
           })
       }
   
       // monta os bimestres ordenados, com somas por matéria e por bimestre
       const bimesters = [...bimMap.values()]
           .sort((a, b) => a.idx - b.idx)
           .map((bim) => {
               const matters = [...bim.matters.values()]
                   .sort((a, b) => a.matter.localeCompare(b.matter, 'pt-BR'))
                   .map((m) => {
                       let somaNota = 0, somaValor = 0
                       for (const a of m.activities) {
                           const n = toNumber(a.nota)
                           const v = toNumber(a.valor)
                           if (!isNaN(n)) somaNota += n
                           if (!isNaN(v)) somaValor += v
                       }
                       return { ...m, somaNota, somaValor }
                   })
   
               let totalNota = 0, totalValor = 0, qtd = 0
               for (const m of matters) {
                   totalNota += m.somaNota
                   totalValor += m.somaValor
                   qtd += m.activities.length
               }
               return { ...bim, matters, totalNota, totalValor, qtd }
           })
   
       return { bimesters, periodLabel }
   }
   
   /* ---------- componente ------------------------------------------------------ */
   
   const ActivitiesModal = ({
       open,
       onClose,
       studentId,
       studentName,
       schoolName,
       idSchool,
       logoUrl: logoUrlProp,
   }) => {
       const [year, setYear] = useState(new Date().getFullYear())
       // ano letivo atual da escola — teto do seletor de anos
       const [anoLetivoAtual, setAnoLetivoAtual] = useState(new Date().getFullYear())
       const [grades, setGrades] = useState([])
       const [loading, setLoading] = useState(false)
       const [ , setLogoUrl] = useState(logoUrlProp || '')
       const cancelled = useRef(false)
   
       // id da escola: prop → sessionStorage (mesma origem do resto do app)
       const resolveSchoolId = () => {
           if (idSchool) return idSchool
           try {
               const s = JSON.parse(sessionStorage.getItem('id-school'))
               if (s) return s
           } catch (e) { /* ignora */ }
           return null
       }
       const schoolId = resolveSchoolId()
       // regime de avaliação do ANO exibido (bimestre ou trimestre)
       const regimeAno = useRegimeDoAno(open ? schoolId : null, year)
   
       // ao abrir: ano letivo da escola (backend); fallback = ano atual
       useEffect(() => {
           if (!open) return
           let active = true
           ;(async () => {
               if (!schoolId) { if (active) setYear(new Date().getFullYear()); return }
               try {
                   const res = await getSchoolYear(schoolId)
                   if (!active) return
                   const y = parseInt(res?.data?.data, 10)
                   const anoValido = !isNaN(y) ? y : new Date().getFullYear()
                   setYear(anoValido)
                   setAnoLetivoAtual(anoValido) // teto do seletor
               } catch (e) {
                   if (active) setYear(new Date().getFullYear())
               }
           })()
           return () => { active = false }
       }, [open, schoolId])
   
       // busca as notas/atividades do ano
       // (cada busca tem a sua própria trava: uma resposta antiga nunca apaga a mais nova)
       useEffect(() => {
           if (!open || !studentId) return
           let ativo = true
           cancelled.current = false
           setLoading(true)
           const load = async () => {
               try {
                   const res = await getNumericalGradesByStudentAndYear(studentId, year)
                   if (!ativo) return
                   setGrades(res?.data?.data || [])
               } catch (err) {
                   if (!ativo) return
                   setGrades([])
               } finally {
                   if (ativo) setLoading(false)
               }
           }
           load()
           return () => { ativo = false; cancelled.current = true }
       }, [open, year, studentId])
   
       // logo da escola (opcional)
       useEffect(() => {
           if (!open) return
           if (logoUrlProp) { setLogoUrl(logoUrlProp); return }
           if (!schoolId) return
   
           const cached = localStorage.getItem(`school-logo-${schoolId}`)
           if (cached) { setLogoUrl(cached); return }
   
           let active = true
           ;(async () => {
               try {
                   const logoRes = await fetchLogo(schoolId)
                   if (active && logoRes?.url) {
                       setLogoUrl(logoRes.url)
                       localStorage.setItem(`school-logo-${schoolId}`, logoRes.url)
                       if (logoRes._id) localStorage.setItem(`school-logo-id-${schoolId}`, logoRes._id)
                   }
               } catch (e) { /* logo é opcional */ }
           })()
           return () => { active = false }
       }, [open, logoUrlProp, schoolId])
   
       if (!open) return null
   
       const { bimesters, periodLabel } = groupGrades(grades, regimeAno)
       const hasData = bimesters.length > 0
   
       return (
           <Overlay onClick={onClose}>
               <ModalCard onClick={(e) => e.stopPropagation()}>
                   <TopBar>
                      {/* <PrintButton onClick={() => window.print()}>Imprimir</PrintButton>*/}
                       <CloseButton onClick={onClose} aria-label="Fechar">
                           <IoClose size={20} />
                       </CloseButton>
                   </TopBar>
   
                   <Body>
                       {/* Seletor de ano — SEMPRE visível (fora do bloco de dados) */}
                       <YearBar>
                           <label style={{ display: 'flex', alignItems: 'center', gap: 10, margin: 0 }}>
                               <span style={{ fontWeight: 700 }}>Ano Letivo:</span>
                               <select
                                   value={year}
                                   onChange={(e) => setYear(Number(e.target.value))}
                                   style={{
                                       padding: '7px 12px',
                                       borderRadius: 8,
                                       border: '1.5px solid var(--line)',
                                       fontSize: 15,
                                       fontWeight: 600,
                                       fontFamily: 'inherit',
                                       color: 'var(--teal-text, var(--teal))',
                                       background: 'var(--surface)',
                                       cursor: 'pointer',
                                       outline: 'none'
                                   }}
                               >
                                   {Array.from({ length: 6 }, (_, i) => anoLetivoAtual - i).map((y) => (
                                       <option key={y} value={y}>
                                           {y}{y === anoLetivoAtual ? ' (atual)' : ''}
                                       </option>
                                   ))}
                               </select>
                           </label>
                       </YearBar>

                       {loading ? (
                           <Loading>Carregando atividades…</Loading>
                       ) : !hasData ? (
                           <Empty>Ainda não há atividades lançadas para {year}.</Empty>
                       ) : (
                           <>
                               <ContLogo>
                                   {/*logoUrl && <Preview src={logoUrl} alt="Logo da escola" />*/}
                                   <h2>Atividades e Notas</h2>
                               </ContLogo>

                               <InfoBox>
                                   <span><strong>Escola:</strong> {schoolName || '—'}</span>
                                   <span><strong>Aluno(a):</strong> {studentName || '—'}</span>
                               </InfoBox>
   
                               {bimesters.map((bim) => (
                                   <BimBlock key={bim.idx}>
                                       <BimHeader>
                                           <h4>{bim.idx + 1}º {periodLabel}</h4>
                                           <BimTotals>
                                               {bim.qtd} {bim.qtd === 1 ? 'atividade' : 'atividades'}
                                               {' · '}
                                               {/*Total: {fmt(bim.totalNota)}
                                               {bim.totalValor > 0 ? ` / ${fmt(bim.totalValor)}` : ''}*/}
                                           </BimTotals>
                                       </BimHeader>
   
                                       {bim.matters.length === 0 ? (
                                           <NoActivities>Nenhuma atividade neste período.</NoActivities>
                                       ) : (
                                           bim.matters.map((m) => (
                                               <MatterBlock key={m.matter}>
                                                   <MatterTitle>
                                                       <span>{m.matter}</span>
                                                       <MatterSum>
                                                           {fmt(m.somaNota)}
                                                           {m.somaValor > 0 ? ` / ${fmt(m.somaValor)}` : ''}
                                                       </MatterSum>
                                                   </MatterTitle>
   
                                                   <ActTable>
                                                       <ActHead>Atividade</ActHead>
                                                       <ActHead $align="center">Valor</ActHead>
                                                       <ActHead $align="center">Nota</ActHead>
   
                                                       {m.activities.map((a) => (
                                                           <React.Fragment key={a.id}>
                                                               <ActName>
                                                                   {a.descricao}
                                                                   {a.tipo && <ActType>{a.tipo}</ActType>}
                                                               </ActName>
                                                               <ActCell $align="center">
                                                                   <ActValue>{fmt(a.valor)}</ActValue>
                                                               </ActCell>
                                                               <ActCell $align="center">
                                                                   <ActGrade $grade={a.nota} $valor={a.valor}>
                                                                       {fmt(a.nota)}
                                                                   </ActGrade>
                                                               </ActCell>
                                                           </React.Fragment>
                                                       ))}
                                                   </ActTable>
                                               </MatterBlock>
                                           ))
                                       )}
                                   </BimBlock>
                               ))}
                           </>
                       )}
                   </Body>
               </ModalCard>
           </Overlay>
       )
   }
   
   export default ActivitiesModal